import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";

/**
 * A photograph that drifts very gently as it scrolls — the work is meant to be
 * admired, so the image is shown almost whole and never cropped aggressively.
 */
function ParallaxPhoto({ src, alt, position = "50% 30%", range = 5, className = "" }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [`-${range}%`, `${range}%`]);
    return (
        <div ref={ref} className={`relative overflow-hidden ${className}`}>
            <motion.div className="absolute" style={{ inset: "-8% 0", y }}>
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: position }}
                />
            </motion.div>
        </div>
    );
}

// Discreet index marker: 01 with a short hairline beneath.
function Index({ n, dark = false }) {
    return (
        <div>
            <span
                className="font-sans"
                style={{
                    fontSize: "0.8rem",
                    letterSpacing: "0.3em",
                    color: dark ? "rgba(184,154,106,0.95)" : "var(--accent)",
                }}
            >
                {n}
            </span>
            <div
                aria-hidden="true"
                className="mt-4"
                style={{
                    width: "56px",
                    height: "1px",
                    backgroundColor: dark
                        ? "rgba(252,251,248,0.35)"
                        : "var(--line)",
                }}
            />
        </div>
    );
}

// "VEZI LUCRAREA →" — quiet editorial link with a nudging arrow.
function WorkLink({ dark = false, testId, slug }) {
    const navigate = useNavigate();
    const color = dark ? "rgba(252,251,248,0.9)" : "var(--ink)";
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={() => {
                try {
                    sessionStorage.setItem(
                        "ladyart_home_scroll",
                        String(Math.round(window.scrollY)),
                    );
                } catch (e) {
                    /* ignore */
                }
                navigate(`/servicii/${slug}`);
            }}
            className="group mt-10 inline-flex items-center gap-4 border-0 bg-transparent p-0"
            style={{
                cursor: "pointer",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.72rem",
                fontWeight: 500,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color,
            }}
        >
            <span className="link-underline">Inspiră-te</span>
            <span
                aria-hidden="true"
                className="inline-block transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-2"
            >
                →
            </span>
        </button>
    );
}

const titleStyle = {
    fontWeight: 300,
    lineHeight: 0.98,
    fontSize: "clamp(2.6rem, 5.6vw, 5.2rem)",
};

const captionStyle = {
    color: "var(--muted-foreground)",
    fontSize: "1rem",
    lineHeight: 1.7,
    maxWidth: "26ch",
};

// Reusable text block for a single work.
function WorkText({ n, title, caption, dark = false, testId, slug, align = "left" }) {
    return (
        <div
            className={`flex h-full flex-col justify-center px-8 py-20 md:px-16 lg:px-24 ${
                align === "right" ? "md:items-end md:text-right" : ""
            }`}
        >
            <Reveal>
                <Index n={n} dark={dark} />
                <h3
                    className="mt-10 font-serif"
                    style={{
                        ...titleStyle,
                        color: dark ? "var(--bg)" : "var(--ink)",
                    }}
                >
                    {title}
                </h3>
                <p
                    className={`mt-8 font-sans ${align === "right" ? "md:ml-auto" : ""}`}
                    style={{
                        ...captionStyle,
                        color: dark ? "rgba(252,251,248,0.7)" : "var(--muted-foreground)",
                    }}
                >
                    {caption}
                </p>
                <WorkLink dark={dark} testId={testId} slug={slug} />
            </Reveal>
        </div>
    );
}

export default function Services() {
    return (
        <section
            id="servicii"
            data-testid="services"
            style={{ backgroundColor: "var(--bg-2)" }}
        >
            {/* ——— Opener ——————————————————————————————————————— */}
            <div className="mx-auto max-w-[1700px] px-8 pb-8 pt-28 md:px-16 md:pb-16 md:pt-44">
                <Reveal>
                    <span className="eyebrow">Servicii</span>
                </Reveal>
                <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <Reveal delay={0.06}>
                        <h2
                            className="font-serif"
                            style={{
                                fontSize: "clamp(2.8rem, 7vw, 6.5rem)",
                                lineHeight: 0.94,
                                fontWeight: 300,
                            }}
                        >
                            Rezultatele vorbesc
                        </h2>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p
                            className="font-sans md:text-right"
                            style={{ ...captionStyle, maxWidth: "24ch" }}
                        >
                            Tehnici moderne, produse profesionale și rezultate
                            naturale, adaptate fiecărei cliente.
                        </p>
                    </Reveal>
                </div>
            </div>

            {/* ——— 01 · AirTouch — title left, large photo right ——— */}
            <div
                data-testid="service-01"
                className="grid grid-cols-1 md:min-h-[90vh] md:grid-cols-2"
            >
                <div className="order-2 md:order-1">
                    <WorkText
                        n="01"
                        title="AirTouch"
                        caption="Tranziții fine de culoare, construite fir cu fir pentru un rezultat natural și luminos."
                        testId="work-link-01"
                        slug="airtouch"
                    />
                </div>
                <div className="order-1 h-[60vh] md:order-2 md:h-auto">
                    <ParallaxPhoto
                        src="/photos/airtouch.webp"
                        alt="AirTouch — blond construit fir cu fir la LadyArt"
                        position="50% 28%"
                        className="h-full w-full"
                    />
                </div>
            </div>

            {/* ——— 02 · Balayage — photo left, title right ————————— */}
            <div
                data-testid="service-02"
                className="grid grid-cols-1 md:min-h-[90vh] md:grid-cols-2"
            >
                <div className="h-[60vh] md:h-auto">
                    <ParallaxPhoto
                        src="/photos/balayage.webp"
                        alt="Balayage — culoare pictată manual la LadyArt"
                        position="50% 32%"
                        className="h-full w-full"
                    />
                </div>
                <div>
                    <WorkText
                        n="02"
                        title="Balayage"
                        caption="Reflexii pictate manual, cu profunzime și dimensiune naturală."
                        testId="work-link-02"
                        slug="balayage"
                        align="right"
                    />
                </div>
            </div>

            {/* ——— 03 · Corecție de culoare — dark editorial band ——— */}
            {/* The one surprise: a full-width ink band, text held left, the work
                running wide to the right edge. */}
            <div
                data-testid="service-03"
                className="relative flex flex-col md:block md:min-h-[92vh]"
                style={{ backgroundColor: "var(--ink)" }}
            >
                <div className="order-1 h-[62vh] md:absolute md:inset-y-0 md:right-0 md:h-auto md:w-[58%]">
                    <ParallaxPhoto
                        src="/photos/img4.webp"
                        alt="Corecție de culoare — readucem echilibrul culorii"
                        position="50% 38%"
                        range={4}
                        className="h-full w-full"
                    />
                </div>
                <div className="relative z-10 order-2 md:flex md:h-full md:w-[46%] md:items-center">
                    <WorkText
                        n="03"
                        title="Corecție de culoare"
                        caption="Echilibrăm nuanța și redăm armonia culorii, cu grijă pentru sănătatea firului."
                        dark
                        testId="work-link-03"
                        slug="corectie-culoare"
                    />
                </div>
            </div>

            {/* ——— 04 · Styling — title left, vertical photo right ——— */}
            <div
                data-testid="service-04"
                className="grid grid-cols-1 md:min-h-[94vh] md:grid-cols-12"
            >
                <div className="order-2 md:order-1 md:col-span-6">
                    <WorkText
                        n="04"
                        title="Styling"
                        caption="Coafuri de eveniment și volum editorial, finisate pentru fotografii și pentru viața reală."
                        testId="work-link-04"
                        slug="styling"
                    />
                </div>
                <div className="order-1 h-[70vh] md:order-2 md:col-span-6 md:h-auto">
                    <ParallaxPhoto
                        src="/photos/styling.webp"
                        alt="Styling — coafură de mireasă cu voal și accesoriu cristal la LadyArt"
                        position="50% 32%"
                        range={6}
                        className="h-full w-full"
                    />
                </div>
            </div>

            {/* ——— Closing tagline ————————————————————————————————— */}
            <div className="flex items-center justify-center gap-6 py-16 md:py-24">
                <span
                    aria-hidden="true"
                    style={{ width: "48px", height: "1px", backgroundColor: "var(--accent)", opacity: 0.6 }}
                />
                <span
                    className="font-sans"
                    style={{
                        fontSize: "0.74rem",
                        letterSpacing: "0.34em",
                        textTransform: "uppercase",
                        color: "var(--accent)",
                    }}
                >
                    Frumusețea ta, creația noastră
                </span>
                <span
                    aria-hidden="true"
                    style={{ width: "48px", height: "1px", backgroundColor: "var(--accent)", opacity: 0.6 }}
                />
            </div>
        </section>
    );
}
